// virtual-medic-frontend/src/components/CaseManager/CaseList.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  InputAdornment,
  Stack,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Publish as PublishIcon,
  UnpublishedOutlined as UnpublishIcon,
  Public as PublicIcon,
  Search as SearchIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api, { virtualMedicAPI } from '../../services/api';
import { getCurrentUser } from '../../services/auth';
import { isGlobalAdmin, isTenantScopedAdmin, isVirtualMedicInstructor } from '../../utils/role';
import {
  caseManagerHeroSx,
  caseManagerPageSx,
  caseManagerPalette,
  caseManagerSelectSx,
  caseManagerSurfaceSx,
  CaseManagerHeroSymbol,
  publicBodyFontFamily,
  publicHeadingFontFamily,
  publicPrimaryButtonSx,
  publicTabSx,
  publicTextFieldSx,
} from './caseManagerStyles';

const formatDateTime = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleString();
};

const getAvailabilitySummary = (caseData, environment, options = {}) => {
  const { isIndividualUser = false } = options;
  if (!caseData) {
    return { label: 'Unavailable', color: 'default', state: 'unavailable' };
  }

  if (caseData.isDraft) {
    return { label: 'Draft', color: 'warning', state: 'draft' };
  }

  const isPublished = isIndividualUser
    ? Boolean(caseData.environments?.testing?.published || caseData.environments?.production?.published)
    : Boolean(caseData.environments?.[environment]?.published);

  if (!isPublished) {
    return { label: 'Not published', color: 'default', state: 'unpublished' };
  }

  const scheduling = caseData.visibility?.scheduling || {};
  const now = Date.now();

  if (scheduling.alwaysAvailable === false) {
    const start = scheduling.startsAt ? new Date(scheduling.startsAt).getTime() : null;
    if (start && now < start) {
      return {
        label: `Opens ${formatDateTime(scheduling.startsAt)}`,
        color: 'info',
        state: 'scheduled',
      };
    }

    const end = scheduling.endsAt ? new Date(scheduling.endsAt).getTime() : null;
    if (end && now > end) {
      return {
        label: `Expired ${formatDateTime(scheduling.endsAt)}`,
        color: 'error',
        state: 'expired',
      };
    }
  }

  return {
    label: 'Available now',
    color: 'success',
    state: 'available',
  };
};

const getRestrictionSummary = (caseData) => {
  const cohort = caseData.visibility?.cohort || {};
  const restrictions = [];

  if (cohort.allowAllGroups === false) {
    const count = Array.isArray(cohort.allowedGroupIds) ? cohort.allowedGroupIds.length : 0;
    restrictions.push({
      type: 'groups',
      label: count > 0 ? `${count} group${count === 1 ? '' : 's'}` : 'Group restricted',
    });
  }

  if (Array.isArray(cohort.allowedYearLevels) && cohort.allowedYearLevels.length > 0) {
    const count = cohort.allowedYearLevels.length;
    restrictions.push({
      type: 'years',
      label: `${count} year${count === 1 ? '' : 's'}`,
    });
  }

  return restrictions;
};

const ORIGIN_LABELS = {
  'virtual-medic': 'Virtual Medic',
  tenant: 'Tenant',
  external: 'External'
};

const CaseList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('my');
  const [cases, setCases] = useState([]);
  const [allCases, setAllCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [environment, setEnvironment] = useState('testing'); // Default to testing
  const currentUser = useMemo(() => getCurrentUser(), []);
  const isPrivilegedUser = isTenantScopedAdmin(currentUser)
    || isVirtualMedicInstructor(currentUser)
    || isGlobalAdmin(currentUser);
  const isIndividualUser = currentUser?.accountType === 'individual' && !isPrivilegedUser;
  const filterEnvironment = isIndividualUser ? 'all' : environment;
  const canCreateCases = Boolean(currentUser);

  const [vmCases, setVmCases] = useState([]);
  const [vmLoading, setVmLoading] = useState(false);
  const [vmError, setVmError] = useState('');
  const [vmFilters, setVmFilters] = useState({
    search: '',
    presentingComplaint: 'all',
    specialty: 'all',
    difficulty: 'all'
  });
  const canAccessVmLibrary = useMemo(
    () => isTenantScopedAdmin(currentUser) || isVirtualMedicInstructor(currentUser),
    [currentUser]
  );
  const [importDialog, setImportDialog] = useState({
    open: false,
    vmCase: null,
    environment: 'testing',
    loading: false,
    success: ''
  });
  const [tenantGroups, setTenantGroups] = useState([]);
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    case: null,
    environment: 'production',
    groupId: '',
    yearLevelsInput: '',
    loading: false,
    result: null,
  });

  // Filter states
  const [categoryType, setCategoryType] = useState('specialty');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt-desc');
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');

  // Data for filters
  const [categories, setCategories] = useState({
    presentingComplaint: [],
    specialty: []
  });
  const vmCaseVersionMap = useMemo(() => {
    const map = new Map();
    vmCases.forEach((vmCase) => {
      map.set((vmCase._id || vmCase.id || '').toString(), vmCase);
    });
    return map;
  }, [vmCases]);

  const vmPresentingComplaints = useMemo(() => {
    return Array.from(new Set(
      vmCases.map((c) => c.presentingComplaint).filter(Boolean)
    )).sort();
  }, [vmCases]);

  const vmSpecialties = useMemo(() => {
    return Array.from(new Set(
      vmCases.map((c) => c.specialty).filter(Boolean)
    )).sort();
  }, [vmCases]);

  // UI state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, case: null });
  const [publishDialog, setPublishDialog] = useState({ open: false, case: null, environment: '' });

  useEffect(() => {
    loadCases();
    loadCategories();
    loadGroups();

    // Show success message if redirected from editor
    if (location.state?.message) {
      setError(''); // Clear any existing errors
      setTimeout(() => {
        const alertDiv = document.createElement('div');
        alertDiv.innerHTML = `<div class="MuiAlert-root MuiAlert-standardSuccess" style="margin-bottom: 16px;">${location.state.message}</div>`;
        const container = document.querySelector('.main-content');
        if (container) {
          container.prepend(alertDiv);
          setTimeout(() => alertDiv.remove(), 5000);
        }
      }, 100);
    }
    // Initialization functions are intentionally excluded to avoid refetch loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    filterAndSortCases();
    // The explicit filter inputs are the complete recomputation trigger set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCases, categoryType, selectedCategory, sortBy, rightSearch, filterEnvironment]);

  useEffect(() => {
    // Preload VM cases to enable update awareness even on My Cases tab
    if (canAccessVmLibrary) {
      loadVmCases();
    }
    // Access changes, rather than callback identity, control this preload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessVmLibrary]);

  useEffect(() => {
    if (!canAccessVmLibrary && activeTab === 'virtual-medic') {
      setActiveTab('my');
    }
  }, [canAccessVmLibrary, activeTab]);

  useEffect(() => {
    if (activeTab === 'virtual-medic' && canAccessVmLibrary) {
      loadVmCases();
    }
    // The query values below intentionally define the library reload boundary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, vmFilters.search, vmFilters.presentingComplaint, vmFilters.specialty, vmFilters.difficulty, canAccessVmLibrary]);

  const loadCases = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cases?limit=1000');
      setAllCases(response.data.cases);
      setError('');
    } catch (err) {
      console.error('Error loading cases:', err);
      setError('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const loadVmCases = async () => {
    try {
      setVmLoading(true);
      setVmError('');
      const response = await virtualMedicAPI.listLibraryCases({
        search: vmFilters.search || undefined,
        presentingComplaint: vmFilters.presentingComplaint !== 'all' ? vmFilters.presentingComplaint : undefined,
        specialty: vmFilters.specialty !== 'all' ? vmFilters.specialty : undefined,
        difficulty: vmFilters.difficulty !== 'all' ? vmFilters.difficulty : undefined,
        limit: 500
      });
      setVmCases(response.data.cases || []);
    } catch (err) {
      console.error('Error loading Virtual Medic cases:', err);
      setVmError('Failed to load Virtual Medic library.');
    } finally {
      setVmLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const [complaintsRes, specialtiesRes] = await Promise.all([
        api.get('/platform/presets/presenting-complaints'),
        api.get('/platform/presets/specialties')
      ]);

      const normalizePresetPayload = (payload) => {
        const source = payload || {};
        if (Array.isArray(source.items)) {
          return source.items;
        }
        if (Array.isArray(source.all)) {
          return source.all;
        }
        return Array.isArray(source) ? source : [];
      };

      const complaintPresets = normalizePresetPayload(complaintsRes.data);
      const specialtyPresets = normalizePresetPayload(specialtiesRes.data);

      const allComplaints = [...new Set([
        ...complaintPresets.map(pc => pc.name),
        ...allCases.map(c => c.presentingComplaint)
      ])].filter(Boolean).sort();

      const allSpecialties = [...new Set([
        ...specialtyPresets.map(spec => spec.name),
        ...allCases.map(c => c.specialty)
      ])].filter(Boolean).sort();

      setCategories({
        presentingComplaint: allComplaints,
        specialty: allSpecialties
      });
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await api.get('/groups');
      setTenantGroups(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error loading groups:', err);
    }
  };

  const handleOpenPreview = (caseData) => {
    const defaultYearLevels = Array.isArray(caseData.visibility?.cohort?.allowedYearLevels)
      ? caseData.visibility.cohort.allowedYearLevels.join(', ')
      : '';

    const previewEnvironment = isIndividualUser
      ? 'testing'
      : (environment === 'production' ? 'production' : 'testing');

    setPreviewDialog({
      open: true,
      case: caseData,
      environment: previewEnvironment,
      groupId: '',
      yearLevelsInput: defaultYearLevels,
      loading: false,
      result: null,
    });
  };

  const handleClosePreview = () => {
    setPreviewDialog({
      open: false,
      case: null,
      environment: isIndividualUser ? 'testing' : 'production',
      groupId: '',
      yearLevelsInput: '',
      loading: false,
      result: null,
    });
  };

  const handleRunPreview = async () => {
    if (!previewDialog.case) {
      return;
    }

    setPreviewDialog((prev) => ({ ...prev, loading: true, result: null }));

    try {
      const params = {
        environment: previewDialog.environment,
        previewMode: 'simulate',
      };

      if (previewDialog.groupId) {
        params.previewGroupIds = previewDialog.groupId;
      }

      const yearLevels = previewDialog.yearLevelsInput
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);

      if (yearLevels.length) {
        params.previewYearLevels = yearLevels
          .map((value) => value.toLowerCase())
          .join(',');
      }

      await api.get(`/unity/cases/${previewDialog.case._id}`, { params });

      setPreviewDialog((prev) => ({
        ...prev,
        loading: false,
        result: { allowed: true, message: 'Learners matching this audience can access the case.' },
      }));
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Preview failed.';
      const code = err.response?.data?.code || err.response?.status || null;

      setPreviewDialog((prev) => ({
        ...prev,
        loading: false,
        result: { allowed: false, message, code },
      }));
    }
  };

  const filterAndSortCases = () => {
    let filtered = [...allCases];

    // Filter by environment
    filtered = filtered.filter(c => isVisibleInEnv(c, filterEnvironment));

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(case_data => {
        if (categoryType === 'presentingComplaint') {
          return case_data.presentingComplaint === selectedCategory;
        } else {
          return case_data.specialty === selectedCategory;
        }
      });
    }

    // Filter by right search
    if (rightSearch.trim()) {
      filtered = filtered.filter(case_data =>
        case_data.title.toLowerCase().includes(rightSearch.toLowerCase()) ||
        case_data.patientName.toLowerCase().includes(rightSearch.toLowerCase())
      );
    }

    // Sort cases
    const [field, order] = sortBy.split('-');
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (field) {
        case 'createdAt':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'difficulty':
          const diffOrder = { 'Core': 1, 'Intermediate': 2, 'Advanced': 3 };
          aValue = diffOrder[a.difficulty] || 4;
          bValue = diffOrder[b.difficulty] || 4;
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        default:
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setCases(filtered);
  };

  const handleDeleteCase = async () => {
    try {
      await api.delete(`/cases/${deleteDialog.case._id}`);
      setDeleteDialog({ open: false, case: null });
      loadCases();
    } catch (err) {
      console.error('Error deleting case:', err);
      setError('Failed to delete case');
    }
  };

  const handlePublishCase = async () => {
    try {
      // Additional safety check for draft cases
      if (publishDialog.case?.isDraft) {
        setError('Cannot publish incomplete case. Please complete the case first.');
        setPublishDialog({ open: false, case: null, environment: '' });
        return;
      }

      const targetCase = publishDialog.case;
      if (!targetCase) {
        return;
      }

      if (isIndividualUser) {
        const caseId = targetCase._id;
        const wasTestingPublished = Boolean(targetCase.environments?.testing?.published);
        const wasProductionPublished = Boolean(targetCase.environments?.production?.published);
        const isCurrentlyPublished = wasTestingPublished || wasProductionPublished;

        if (isCurrentlyPublished) {
          if (wasProductionPublished) {
            await api.post(`/cases/${caseId}/unpublish`, { environment: 'production' });
          }
          if (wasTestingPublished || wasProductionPublished) {
            await api.post(`/cases/${caseId}/unpublish`, { environment: 'testing' });
          }
        } else {
          await api.post(`/cases/${caseId}/publish`, { environment: 'testing' });
        }
      } else {
        const action = isPublished(targetCase, publishDialog.environment)
          ? 'unpublish' : 'publish';

        await api.post(`/cases/${targetCase._id}/${action}`, {
          environment: publishDialog.environment
        });
      }

      setPublishDialog({ open: false, case: null, environment: '' });
      loadCases();
    } catch (err) {
      console.error('Error updating case publication:', err);
      setError(err.response?.data?.message || 'Failed to update case publication');
    }
  };

  const handleOpenVmImport = (vmCase) => {
    setImportDialog({
      open: true,
      vmCase,
      environment: 'testing',
      loading: false,
      success: ''
    });
  };

  const handleCloseImportDialog = () => {
    setImportDialog({
      open: false,
      vmCase: null,
      environment: 'testing',
      loading: false,
      success: ''
    });
  };

  const handleImportVmCase = async () => {
    if (!importDialog.vmCase) {
      return;
    }
    setImportDialog((prev) => ({ ...prev, loading: true, success: '' }));
    try {
      await virtualMedicAPI.importCase(importDialog.vmCase._id || importDialog.vmCase.id, {
        targetEnvironment: importDialog.environment
      });
      setImportDialog((prev) => ({ ...prev, loading: false, success: 'Case imported successfully.' }));
      loadCases();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to import case.';
      setImportDialog((prev) => ({ ...prev, loading: false, success: '' }));
      setVmError(message);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Core': return 'success';
      case 'Intermediate': return 'warning';
      case 'Advanced': return 'error';
      default: return 'default';
    }
  };

  const renderProvenanceBadges = (caseData) => {
    if (!caseData) {
      return null;
    }

    const provenance = caseData.provenance || {};
    const badges = [];
    const userId = currentUser?._id || currentUser?.id;
    const authorId = caseData.author?._id || caseData.author?.id || caseData.author;
    const isCommunityImport = provenance.originType === 'community'
      && Boolean(provenance.originCaseId || provenance.sourceCaseId);
    const isCreatedByUser = Boolean(userId && authorId && userId.toString() === authorId.toString());

    if (provenance.originType === 'virtual-medic' && caseData.tenantId) {
      badges.push(
        <Chip key="vm-origin" size="small" variant="outlined" color="info" label="Imported from Virtual Medic" />
      );

      const sourceId = provenance.sourceCaseId || provenance.originCaseId;
      const vmCase = sourceId ? vmCaseVersionMap.get(sourceId.toString()) : null;
      const hasNewerVersion = vmCase && Number.isFinite(vmCase.version)
        && Number.isFinite(provenance.lastOriginVersion)
        && vmCase.version > provenance.lastOriginVersion;

      if (hasNewerVersion) {
        badges.push(
          <Chip
            key="vm-update"
            size="small"
            color="warning"
            variant="outlined"
            label={`Newer VM version available (v${vmCase.version})`}
            onClick={() => vmCase && handleOpenVmImport(vmCase)}
          />
        );
      }
    }

    if (isCommunityImport) {
      badges.push(
        <Chip key="community-origin" size="small" variant="outlined" color="secondary" label="Community import" />
      );
    } else if (isCreatedByUser) {
      badges.push(
        <Chip key="created-by" size="small" variant="outlined" color="success" label="Created by you" />
      );
    }

    if (provenance.originType && provenance.originType !== 'virtual-medic' && !isCommunityImport) {
      badges.push(
        <Chip
          key="origin"
          size="small"
          variant="outlined"
          label={`Origin: ${ORIGIN_LABELS[provenance.originType] || provenance.originType}`}
        />
      );
    }

    if (!badges.length) {
      return null;
    }

    return (
      <Box sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1}>
          {badges}
        </Stack>
      </Box>
    );
  };

  const isCasePublished = (case_data) => (
    Boolean(case_data?.environments?.testing?.published || case_data?.environments?.production?.published)
  );

  const isPublished = (case_data, env) => {
    if (!case_data || !case_data.environments || !case_data.environments[env]) {
      return false;
    }
    return case_data.environments[env].published || false;
  };

  const isVisibleInEnv = (case_data, env) => {
    if (env === 'testing') {
      // show everything that is either explicitly published to testing
      // OR is still a draft
      return case_data.isDraft || (case_data.environments?.testing?.published);
    }
    if (env === 'production') {
      return case_data.environments?.production?.published;
    }
    // "all"  – show absolutely everything
    return true;
  };

  const getFilteredCategories = () => {
    if (!categories[categoryType]) return [];

    if (!leftSearch.trim()) {
      return categories[categoryType];
    }

    return categories[categoryType].filter(cat =>
      cat.toLowerCase().includes(leftSearch.toLowerCase())
    );
  };

  // Calculate counts based on ALL cases for the current environment, not filtered cases
  const getCategoryCount = (category) => {
    const environmentCases = allCases.filter(c => isVisibleInEnv(c, filterEnvironment));

    if (category === 'all') {
      return environmentCases.length;
    }

    return environmentCases.filter(case_data => {
      if (categoryType === 'presentingComplaint') {
        return case_data.presentingComplaint === category;
      } else {
        return case_data.specialty === category;
      }
    }).length;
  };

  const dialogIsPublished = isIndividualUser
    ? isCasePublished(publishDialog.case)
    : isPublished(publishDialog.case, publishDialog.environment);
  const dialogEnvironmentLabel = isIndividualUser ? 'Library' : publishDialog.environment;
  const dialogActionLabel = isIndividualUser
    ? (dialogIsPublished ? 'Hide' : 'Publish')
    : (dialogIsPublished ? 'Unpublish' : 'Publish');

  if (loading) {
    return (
      <Container maxWidth="xl" sx={caseManagerPageSx}>
        <Box
          sx={{
            ...caseManagerSurfaceSx,
            py: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: caseManagerPalette.heroBlueMid }} />
          <Typography sx={{ color: caseManagerPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
            Loading cases...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={caseManagerPageSx}>
      <Box sx={{ ...caseManagerHeroSx, mb: 2 }}>
        <CaseManagerHeroSymbol />
        <Grid container spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} lg={8}>
            <Stack spacing={1}>
              <Chip
                label={activeTab === 'my' ? 'Case management' : 'Virtual Medic library'}
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: alpha('#ffffff', 0.12),
                  color: caseManagerPalette.white,
                  borderRadius: '999px',
                  fontWeight: 800,
                  fontFamily: publicBodyFontFamily,
                }}
              />
              <Typography
                component="h1"
                sx={{
                  fontFamily: publicHeadingFontFamily,
                  fontSize: { xs: '1.95rem', md: '2.45rem' },
                  lineHeight: 1.08,
                  letterSpacing: 0,
                }}
              >
                {activeTab === 'my' ? 'Build, organise, and publish cases' : 'Import from the Virtual Medic library'}
              </Typography>
              <Typography
                sx={{
                  maxWidth: 760,
                  color: alpha('#ffffff', 0.84),
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  lineHeight: 1.55,
                  fontFamily: publicBodyFontFamily,
                }}
              >
                {activeTab === 'my'
                  ? 'Keep drafts, published cases, previews, and audience controls in one calmer workspace.'
                  : 'Browse reusable Virtual Medic cases, filter by topic or difficulty, and import them into your own tenant environment.'}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4}>
            {canCreateCases && activeTab === 'my' && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => navigate('/cases/new')}
                sx={{
                  ...publicPrimaryButtonSx,
                  width: { xs: '100%', lg: 'auto' },
                  minHeight: 44,
                  bgcolor: caseManagerPalette.white,
                  color: caseManagerPalette.heroBlueDeep,
                  '&:hover': {
                    bgcolor: alpha('#ffffff', 0.92),
                  },
                }}
              >
                Create New Case
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>

      <Paper sx={{ ...caseManagerSurfaceSx, p: 0.8, mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_event, value) => setActiveTab(value)}
          sx={{
            minHeight: 0,
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          <Tab
            label="My Cases"
            value="my"
            sx={{
              ...publicTabSx,
              borderRadius: '16px',
              color: caseManagerPalette.inkSoft,
              '&.Mui-selected': {
                color: caseManagerPalette.heroBlueDeep,
                bgcolor: alpha(caseManagerPalette.heroBlueMid, 0.10),
              },
            }}
          />
          {canAccessVmLibrary && (
            <Tab
              label="Virtual Medic Library"
              value="virtual-medic"
              sx={{
                ...publicTabSx,
                borderRadius: '16px',
                color: caseManagerPalette.inkSoft,
                '&.Mui-selected': {
                  color: caseManagerPalette.heroBlueDeep,
                  bgcolor: alpha(caseManagerPalette.heroBlueMid, 0.10),
                },
              }}
            />
          )}
        </Tabs>
      </Paper>

      {activeTab === 'my' && error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '20px' }}>
          {error}
        </Alert>
      )}

      {activeTab === 'my' ? (
      <Box display="flex" gap={1.8} sx={{ flexDirection: { xs: 'column', lg: 'row' }, minHeight: { lg: 'calc(100vh - 210px)' } }}>
        {/* Left Panel - Filters */}
        <Box sx={{ width: { xs: '100%', lg: 300 }, flexShrink: 0 }}>
          <Paper sx={{ ...caseManagerSurfaceSx, p: 1.7, height: '100%', overflow: 'hidden' }}>
            {/* Environment Selector */}
            {isIndividualUser ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Library view</Typography>
                <Typography variant="caption" color="text.secondary">
                  Showing drafts and published cases.
                </Typography>
              </Box>
            ) : (
              <FormControl fullWidth size="small" sx={{ ...caseManagerSelectSx, mb: 2 }}>
                <InputLabel>Environment</InputLabel>
                <Select
                  value={environment}
                  label="Environment"
                  onChange={(e) => setEnvironment(e.target.value)}
                >
                  <MenuItem value="testing">Testing Environment</MenuItem>
                  <MenuItem value="production">Production Environment</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Category Type Selector */}
            <FormControl fullWidth size="small" sx={{ ...caseManagerSelectSx, mb: 2 }}>
              <InputLabel>Category Type</InputLabel>
              <Select
                value={categoryType}
                label="Category Type"
                onChange={(e) => {
                  setCategoryType(e.target.value);
                  setSelectedCategory('all');
                }}
              >
                <MenuItem value="presentingComplaint">By Presenting Complaint</MenuItem>
                <MenuItem value="specialty">By Specialty</MenuItem>
              </Select>
            </FormControl>

            {/* Left Search */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search categories..."
              value={leftSearch}
              onChange={(e) => setLeftSearch(e.target.value)}
              sx={{ ...publicTextFieldSx, mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Category List */}
            <List sx={{ height: { lg: 'calc(100% - 200px)' }, overflow: 'auto' }}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={selectedCategory === 'all'}
                  onClick={() => setSelectedCategory('all')}
                  sx={{
                    borderRadius: '12px',
                    mb: 0.55,
                    bgcolor: selectedCategory === 'all' ? caseManagerPalette.heroBlueDeep : alpha(caseManagerPalette.heroBlueMist, 0.7),
                    color: selectedCategory === 'all' ? caseManagerPalette.white : caseManagerPalette.ink,
                    '&:hover': {
                      bgcolor: selectedCategory === 'all' ? caseManagerPalette.heroBlueDeeper : alpha(caseManagerPalette.heroBlueMid, 0.08)
                    },
                    '&.Mui-selected': {
                      bgcolor: caseManagerPalette.heroBlueDeep,
                      color: caseManagerPalette.white,
                      '&:hover': {
                        bgcolor: caseManagerPalette.heroBlueDeeper
                      }
                    }
                  }}
                >
                  <ListItemText
                    primary="All Cases"
                    secondary={`${getCategoryCount('all')} total`}
                    primaryTypographyProps={{
                      color: 'inherit'
                    }}
                    secondaryTypographyProps={{
                      color: selectedCategory === 'all' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                    }}
                  />
                </ListItemButton>
              </ListItem>

              {getFilteredCategories().map((category) => {
                const count = getCategoryCount(category);

                return (
                  <ListItem key={category} disablePadding>
                    <ListItemButton
                      selected={selectedCategory === category}
                      onClick={() => setSelectedCategory(category)}
                      sx={{
                        borderRadius: '12px',
                        mb: 0.55,
                        bgcolor: selectedCategory === category ? caseManagerPalette.heroBlueDeep : alpha(caseManagerPalette.heroBlueMist, 0.7),
                        color: selectedCategory === category ? caseManagerPalette.white : caseManagerPalette.ink,
                        '&:hover': {
                          bgcolor: selectedCategory === category ? caseManagerPalette.heroBlueDeeper : alpha(caseManagerPalette.heroBlueMid, 0.08)
                        },
                        '&.Mui-selected': {
                          bgcolor: caseManagerPalette.heroBlueDeep,
                          color: caseManagerPalette.white,
                          '&:hover': {
                            bgcolor: caseManagerPalette.heroBlueDeeper
                          }
                        }
                      }}
                    >
                      <ListItemText
                        primary={category}
                        secondary={`${count} case${count !== 1 ? 's' : ''}`}
                        primaryTypographyProps={{
                          color: 'inherit'
                        }}
                        secondaryTypographyProps={{
                          color: selectedCategory === category ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </Box>

        {/* Right Panel - Cases */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper sx={{ ...caseManagerSurfaceSx, p: 1.8, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Right Panel Header */}
            <Typography sx={{ mb: 1.4, color: caseManagerPalette.ink, fontSize: { xs: '1.25rem', md: '1.42rem' }, fontWeight: 800, fontFamily: publicHeadingFontFamily }}>
              {selectedCategory === 'all' ? 'All cases' : selectedCategory}
            </Typography>

            {/* Controls Row */}
            <Box display="flex" gap={1.4} mb={1.5} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
              {/* Sort Dropdown */}
              <FormControl sx={{ ...caseManagerSelectSx, minWidth: { md: 250 } }} size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="createdAt-desc">Date Created (Newest to Oldest)</MenuItem>
                  <MenuItem value="createdAt-asc">Date Created (Oldest to Newest)</MenuItem>
                  <MenuItem value="difficulty-asc">Difficulty (Easiest to Hardest)</MenuItem>
                  <MenuItem value="difficulty-desc">Difficulty (Hardest to Easiest)</MenuItem>
                  <MenuItem value="title-asc">Title (A-Z)</MenuItem>
                  <MenuItem value="title-desc">Title (Z-A)</MenuItem>
                </Select>
              </FormControl>

              {/* Right Search */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search cases..."
                value={rightSearch}
                onChange={(e) => setRightSearch(e.target.value)}
                sx={publicTextFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Cases List - Scrollable */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {cases.map((case_data) => {
                const availability = getAvailabilitySummary(case_data, environment, { isIndividualUser });
                const restrictions = getRestrictionSummary(case_data);
                const communityListingState = case_data.community?.listingState || 'draft';
                const isCommunityEligible = case_data.library?.scope !== 'official'
                  && case_data.provenance?.originType !== 'virtual-medic';
                const communityActionLabel = (communityListingState === 'published' || communityListingState === 'pending')
                  ? 'Manage community listing'
                  : 'Publish to community';

                return (
                <Paper
                  key={case_data._id}
                  sx={{
                    p: { xs: 1.5, md: 1.7 },
                    mb: 1.1,
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    alignItems: { lg: 'center' },
                    gap: 2,
                    borderRadius: '16px',
                    border: `1px solid ${alpha(case_data.isDraft ? '#f59e0b' : caseManagerPalette.ink, case_data.isDraft ? 0.4 : 0.08)}`,
                    backgroundColor: case_data.isDraft
                      ? alpha('#f59e0b', 0.06)
                      : alpha('#ffffff', 0.92),
                    boxShadow: 'none'
                  }}
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
                      <Typography noWrap sx={{ color: caseManagerPalette.ink, fontSize: '1.02rem', fontWeight: 800, fontFamily: publicHeadingFontFamily }}>
                        {case_data.title}
                      </Typography>
                      {case_data.isDraft && (
                        <Chip
                          label="Draft"
                          color="warning"
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                    </Box>

                    <Typography sx={{ mb: 0.8, color: caseManagerPalette.inkSoft, fontSize: '0.88rem', fontFamily: publicBodyFontFamily }}>
                      {case_data.patientName} ({case_data.gender}, {case_data.age}y)
                    </Typography>

                    <Box display="flex" gap={0.7} mb={0.8} flexWrap="wrap">
                      <Chip label={case_data.presentingComplaint} size="small" sx={{ fontFamily: publicBodyFontFamily, fontWeight: 700 }} />
                      <Chip label={case_data.specialty} size="small" variant="outlined" sx={{ fontFamily: publicBodyFontFamily, fontWeight: 700 }} />
                      <Chip
                        label={case_data.difficulty}
                        size="small"
                        color={getDifficultyColor(case_data.difficulty)}
                        sx={{ fontFamily: publicBodyFontFamily, fontWeight: 700 }}
                      />
                    </Box>

                    <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                      <Chip
                        label={availability.label}
                        size="small"
                        color={availability.color}
                        variant={availability.state === 'available' ? 'filled' : 'outlined'}
                      />
                      {restrictions.map((restriction) => (
                        <Chip
                          key={`${case_data._id}-${restriction.type}`}
                          label={restriction.label}
                          size="small"
                          variant="outlined"
                          sx={{ fontFamily: publicBodyFontFamily, fontWeight: 700 }}
                        />
                      ))}
                    </Box>

                    {case_data.visibility?.accessNotes && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontFamily: publicBodyFontFamily }}>
                        {case_data.visibility.accessNotes}
                      </Typography>
                    )}

                    {renderProvenanceBadges(case_data)}

                    {/* Author and editor information */}
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: publicBodyFontFamily }}>
                        Created by {case_data.author?.firstName || 'Unknown'} {case_data.author?.lastName || 'User'}{' '}
                        on {new Date(case_data.createdAt).toLocaleDateString()}
                      </Typography>
                      {case_data.lastModifiedBy && case_data.lastModifiedBy._id !== case_data.author?._id && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: publicBodyFontFamily }}>
                          Last edited by {case_data.lastModifiedBy?.firstName || 'Unknown'} {case_data.lastModifiedBy?.lastName || 'User'}{' '}
                          on {new Date(case_data.lastModifiedAt).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Status indicators */}
                  <Box display="flex" flexDirection="column" alignItems={{ xs: 'flex-start', lg: 'center' }} gap={1} sx={{ minWidth: { lg: 120 } }}>
                    {case_data.isDraft ? (
                      <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 700, fontFamily: publicBodyFontFamily }}>
                        Draft
                      </Typography>
                    ) : isIndividualUser ? (
                      <Typography
                        variant="caption"
                        sx={{ color: isCasePublished(case_data) ? 'success.main' : 'text.secondary', fontWeight: 700, fontFamily: publicBodyFontFamily }}
                      >
                        {isCasePublished(case_data) ? 'Published' : 'Not published'}
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: publicBodyFontFamily }}>
                          Test: {isPublished(case_data, 'testing') ? 'Yes' : 'No'}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: publicBodyFontFamily }}>
                          Prod: {isPublished(case_data, 'production') ? 'Yes' : 'No'}
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* Action buttons */}
                  <Box display="flex" flexDirection="column" gap={1} sx={{ alignSelf: { xs: 'stretch', lg: 'center' } }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                      <Tooltip title="View Case">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/cases/${case_data._id}`)}
                          sx={{ bgcolor: alpha(caseManagerPalette.heroBlueMid, 0.08), color: caseManagerPalette.heroBlueDeep }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit Case">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/cases/${case_data._id}/edit`)}
                          sx={{ bgcolor: alpha(caseManagerPalette.heroBlueMid, 0.08), color: caseManagerPalette.heroBlueDeep }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {!isIndividualUser && (
                        <Tooltip title="Preview as learner">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenPreview(case_data)}
                            sx={{ bgcolor: alpha(caseManagerPalette.heroBlueMid, 0.08), color: caseManagerPalette.heroBlueDeep }}
                          >
                            <PreviewIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={isCommunityEligible ? communityActionLabel : 'Official library cases cannot be published to the community'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/cases/${case_data._id}?publishCommunity=1`)}
                            disabled={!isCommunityEligible}
                            sx={{
                              bgcolor: alpha(caseManagerPalette.heroBlueMid, 0.08),
                              color: caseManagerPalette.heroBlueDeep,
                              opacity: isCommunityEligible ? 1 : 0.4,
                              cursor: isCommunityEligible ? 'pointer' : 'not-allowed'
                            }}
                          >
                            <PublicIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                      {isIndividualUser ? (
                        <Tooltip title={
                          case_data.isDraft
                            ? "Cannot publish draft - complete case first"
                            : (isCasePublished(case_data)
                              ? "Hide this case from your library"
                              : "Publish to your library")
                        }>
                          <span>
                            <IconButton
                              size="small"
                              color={isCasePublished(case_data) ? 'primary' : 'default'}
                              onClick={() => case_data.isDraft ? null : setPublishDialog({
                                open: true,
                                case: case_data,
                                environment: 'testing'
                              })}
                              disabled={case_data.isDraft}
                              sx={{
                                bgcolor: alpha(caseManagerPalette.heroBlueMid, 0.08),
                                opacity: case_data.isDraft ? 0.3 : 1,
                                cursor: case_data.isDraft ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {isCasePublished(case_data) ? <UnpublishIcon /> : <PublishIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title={
                          case_data.isDraft
                            ? "Cannot publish draft - complete case first"
                            : (isPublished(case_data, 'production')
                              ? "Unpublish from Production"
                              : "Publish to Production")
                        }>
                          <span>
                            <IconButton
                              size="small"
                              color={isPublished(case_data, 'production') ? 'primary' : 'default'}
                              onClick={() => case_data.isDraft ? null : setPublishDialog({
                                open: true,
                                case: case_data,
                                environment: 'production'
                              })}
                              disabled={case_data.isDraft}
                              sx={{
                                bgcolor: alpha(caseManagerPalette.heroBlueMid, 0.08),
                                opacity: case_data.isDraft ? 0.3 : 1,
                                cursor: case_data.isDraft ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {isPublished(case_data, 'production') ? <UnpublishIcon /> : <PublishIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      <Tooltip title="Delete Case">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, case: case_data })}
                          sx={{ bgcolor: alpha('#ef4444', 0.08) }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              );
              })}

              {/* Empty State */}
              {cases.length === 0 && (
                <Box textAlign="center" py={8}>
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontFamily: publicHeadingFontFamily, fontWeight: 800 }}>
                    No cases found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontFamily: publicBodyFontFamily }}>
                    {selectedCategory === 'all'
                      ? 'Create your first case to get started'
                      : `No cases found for ${selectedCategory}`
                    }
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/cases/new')}
                    sx={publicPrimaryButtonSx}
                  >
                    Create New Case
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
      ) : (
        <Box>
          {vmError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {vmError}
            </Alert>
          )}
          <Paper sx={{ ...caseManagerSurfaceSx, p: 2.2, mb: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search Virtual Medic cases..."
                value={vmFilters.search}
                onChange={(e) => setVmFilters((prev) => ({ ...prev, search: e.target.value }))}
                sx={publicTextFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ ...caseManagerSelectSx, minWidth: 180 }}>
                <InputLabel>Presenting complaint</InputLabel>
                <Select
                  value={vmFilters.presentingComplaint}
                  label="Presenting complaint"
                  onChange={(e) => setVmFilters((prev) => ({ ...prev, presentingComplaint: e.target.value }))}
                >
                  <MenuItem value="all">All</MenuItem>
                  {vmPresentingComplaints.map((pc) => (
                    <MenuItem key={pc} value={pc}>{pc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ ...caseManagerSelectSx, minWidth: 160 }}>
                <InputLabel>Specialty</InputLabel>
                <Select
                  value={vmFilters.specialty}
                  label="Specialty"
                  onChange={(e) => setVmFilters((prev) => ({ ...prev, specialty: e.target.value }))}
                >
                  <MenuItem value="all">All</MenuItem>
                  {vmSpecialties.map((spec) => (
                    <MenuItem key={spec} value={spec}>{spec}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ ...caseManagerSelectSx, minWidth: 140 }}>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  value={vmFilters.difficulty}
                  label="Difficulty"
                  onChange={(e) => setVmFilters((prev) => ({ ...prev, difficulty: e.target.value }))}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="Core">Core</MenuItem>
                  <MenuItem value="Intermediate">Intermediate</MenuItem>
                  <MenuItem value="Advanced">Advanced</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Paper>

          {vmLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {vmCases.map((vmCase) => (
                <Grid item xs={12} sm={6} md={4} key={vmCase._id || vmCase.id}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom noWrap>
                        {vmCase.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} noWrap>
                        {vmCase.shortDescription || 'No summary provided.'}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                        <Chip label={vmCase.presentingComplaint} size="small" />
                        <Chip label={vmCase.specialty} size="small" variant="outlined" />
                        <Chip label={vmCase.difficulty} size="small" color={getDifficultyColor(vmCase.difficulty)} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Updated {vmCase.updatedAt ? format(new Date(vmCase.updatedAt), 'MMM d, yyyy') : '—'}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                      <Button size="small" onClick={() => handleOpenVmImport(vmCase)}>
                        Import
                      </Button>
                      <Button size="small" disabled>
                        View
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
              {!vmCases.length && !vmLoading && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6">No Virtual Medic cases available</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting filters or check back later.
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </Box>
      )}

      <Dialog open={importDialog.open} onClose={handleCloseImportDialog} fullWidth maxWidth="xs">
        <DialogTitle>Import Virtual Medic Case</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body1">
              {importDialog.vmCase?.title || 'Select a case to import'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose where to place this case. Importing creates a snapshot in your tenant without affecting the Virtual Medic original.
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Target environment</InputLabel>
              <Select
                value={importDialog.environment}
                label="Target environment"
                onChange={(e) => setImportDialog((prev) => ({ ...prev, environment: e.target.value }))}
              >
                <MenuItem value="testing">Testing (recommended)</MenuItem>
                <MenuItem value="production">Production</MenuItem>
              </Select>
            </FormControl>
            {importDialog.success && (
              <Alert severity="success">{importDialog.success}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleImportVmCase}
            disabled={importDialog.loading}
          >
            {importDialog.loading ? 'Importing…' : 'Import case'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewDialog.open}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Test as Learner{previewDialog.case ? ` • ${previewDialog.case.title}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {isIndividualUser
                ? 'Preview learner visibility for this case with optional cohort overrides.'
                : 'Simulate learner visibility by selecting an environment and optional cohort overrides.'}
            </Typography>
            {previewDialog.result && (
              <Alert severity={previewDialog.result.allowed ? 'success' : 'warning'}>
                {previewDialog.result.message}
                {previewDialog.result.code && (
                  <Typography variant="caption" display="block">
                    Code: {previewDialog.result.code}
                  </Typography>
                )}
              </Alert>
            )}
            {!isIndividualUser && (
              <FormControl fullWidth size="small">
                <InputLabel>Environment</InputLabel>
                <Select
                  label="Environment"
                  value={previewDialog.environment}
                  onChange={(event) => setPreviewDialog((prev) => ({
                    ...prev,
                    environment: event.target.value,
                    result: null,
                  }))}
                >
                  <MenuItem value="testing">Testing</MenuItem>
                  <MenuItem value="production">Production</MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Group (optional)</InputLabel>
              <Select
                label="Group (optional)"
                value={previewDialog.groupId}
                onChange={(event) => setPreviewDialog((prev) => ({
                  ...prev,
                  groupId: event.target.value,
                  result: null,
                }))}
              >
                <MenuItem value="">All groups</MenuItem>
                {tenantGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Year levels (comma separated)"
              value={previewDialog.yearLevelsInput}
              onChange={(event) => setPreviewDialog((prev) => ({
                ...prev,
                yearLevelsInput: event.target.value,
                result: null,
              }))}
              helperText="Leave blank to ignore year-level restrictions."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
          <Button
            variant="contained"
            onClick={handleRunPreview}
            disabled={previewDialog.loading || !previewDialog.case}
          >
            {previewDialog.loading ? 'Testing…' : 'Run Preview'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, case: null })}>
        <DialogTitle>Delete Case</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the case "{deleteDialog.case?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, case: null })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteCase} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish/Unpublish Dialog */}
      <Dialog open={publishDialog.open} onClose={() => setPublishDialog({ open: false, case: null, environment: '' })}>
        <DialogTitle>
          {dialogIsPublished
            ? `Hide from ${dialogEnvironmentLabel}`
            : `Publish to ${dialogEnvironmentLabel}`}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {dialogIsPublished ? 'hide' : 'publish'}
            "{publishDialog.case?.title}" {dialogIsPublished ? 'from' : 'to'} {dialogEnvironmentLabel}?
            {!isIndividualUser && publishDialog.environment === 'production' && publishDialog.case && isPublished(publishDialog.case, publishDialog.environment) && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="info">
                  This will move the case back to the testing environment.
                </Alert>
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialog({ open: false, case: null, environment: '' })}>
            Cancel
          </Button>
          <Button onClick={handlePublishCase} color="primary" variant="contained">
            {dialogActionLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CaseList;
